import { useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useMatch } from "react-router-dom";
import "./styles/form.css";
import "./styles/layout.css";
import "./styles/auth.css";
import "./styles/dashboard.css";
import "./styles/application-form.css";
import "./styles/post-application.css";
import "./styles/modals.css";

import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import JoinUsPage from "./pages/JoinUsPage";
import DashboardPage from "./pages/DashboardPage";
import ApplicationFormPage from "./pages/ApplicationFormPage";
import ApplicationCreatedPage from "./pages/ApplicationCreatedPage";
import PostApplicationPage from "./pages/PostApplicationPage";

import LeadCreatedModal from "./components/LeadCreatedModal";
import Spinner from "./components/Spinner";
import BottomSheet from "./components/BottomSheet";
import ConfirmDialog from "./components/ConfirmDialog";
import PreviewModal from "./components/PreviewModal";
import MailModal from "./components/MailModal";
import CancelApplicationModal from "./components/CancelApplicationModal";
import Toast from "./components/Toast";

import { buildSheetItems } from "./utils/sheetItems";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicOnlyRoute from "./routes/PublicOnlyRoute";

import useUiFeedback from "./hooks/useUiFeedback";
import useSession from "./hooks/useSession";
import useDashboardLeads from "./hooks/useDashboardLeads";
import useApplicationForm, { MAX_FINANCIERS, STALE_FINANCIER_MESSAGE } from "./hooks/useApplicationForm";
import useDocuments from "./hooks/useDocuments";
import usePostApplication from "./hooks/usePostApplication";

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // useMatch matches independently of route ranking, so a literal sibling route
  // (e.g. /application/created) would otherwise be mistaken for a numeric
  // :leadId — only accept it when it's actually numeric.
  const applicationLeadMatchRaw = useMatch("/application/:leadId");
  const applicationLeadMatch = /^\d+$/.test(applicationLeadMatchRaw?.params?.leadId || "") ? applicationLeadMatchRaw : null;
  const postLeadMatchRaw = useMatch("/post-application/:leadId");
  const postLeadMatch = /^\d+$/.test(postLeadMatchRaw?.params?.leadId || "") ? postLeadMatchRaw : null;
  const postLeadId = postLeadMatch ? Number(postLeadMatch.params.leadId) : null;
  const onPostApplicationRoute = !!postLeadMatch;

  const { toast, flash, savedAt, setSavedAt, touchSaved } = useUiFeedback();
  const session = useSession({ navigate });
  const dashboard = useDashboardLeads();
  const appForm = useApplicationForm({ navigate, location, applicationLeadMatch, flash, touchSaved, setSavedAt });
  const documents = useDocuments({
    loanId: appForm.loanId,
    active: appForm.step === 3 || onPostApplicationRoute,
    financierList: appForm.financierList,
    selectedBanks: appForm.selectedBanks,
    govIdTypes: appForm.configOptions.govt_id_type,
    flash,
    touchSaved,
  });
  const postApp = usePostApplication({
    setDocs: documents.setDocs,
    setAutoOpen: documents.setAutoOpen,
    seqRef: documents.seqRef,
    selectedBanks: appForm.selectedBanks,
    financierList: appForm.financierList,
    configFinancierList: appForm.configOptions.financier_list,
    fins: appForm.fins,
    v: appForm.v,
    coborrowers: appForm.coborrowers,
    flash,
    touchSaved,
    navigate,
    displayLoanId: appForm.displayLoanId,
    user: session.user,
    loanId: appForm.loanId,
    setLoanId: appForm.setLoanId,
    loadConfigIfNeeded: appForm.loadConfigIfNeeded,
    loadLeadDetails: appForm.loadLeadDetails,
    postLeadId,
    onPostApplicationRoute,
  });

  // Cancel application sheet: { leadId, financierId? } while open.
  const [cancelTarget, setCancelTarget] = useState(null);
  const onApplicationCancelled = () => {
    setCancelTarget(null);
    // The application is Lost now — back to the dashboard, where it shows
    // under Lost and opens read-only.
    if (onPostApplicationRoute) postApp.leavePostApplication();
    else navigate("/dashboard");
  };

  const goHome = () => {
    appForm.setLeadModal(false);
    documents.setSheet(null);
    documents.setConfirm(null);
    documents.setPreview(null);
    navigate("/dashboard");
  };

  const toDash = () => {
    appForm.setLeadModal(false);
    navigate("/dashboard");
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage
                onOpenJoin={() => navigate("/join-us")}
                onOpenForgot={() => navigate("/forgot-password")}
                onSubmit={session.handleLoginSuccess}
              />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnlyRoute>
              <ForgotPasswordPage onClose={() => navigate("/login")} onFlash={flash} />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/join-us"
          element={
            <PublicOnlyRoute>
              <JoinUsPage onClose={() => navigate("/login", { replace: true })} onFlash={flash} />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage
                user={session.user}
                leads={dashboard.leads}
                leadsLoading={dashboard.leadsLoading}
                hasMoreLeads={dashboard.hasMoreLeads}
                leadsTotal={dashboard.leadsTotal}
                loadingMoreLeads={dashboard.loadingMoreLeads}
                onLoadMoreLeads={dashboard.loadMoreLeads}
                buckets={dashboard.tileBuckets}
                q={dashboard.q}
                filter={dashboard.filter}
                onSearch={dashboard.setQ}
                onFilterToggle={dashboard.setFilter}
                onCreateApplication={appForm.startNew}
                onOpenLead={postApp.openPost}
                onContinueLead={appForm.continueLead}
                onLogout={session.handleLogout}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/application/:leadId?"
          element={
            <ProtectedRoute>
              <ApplicationFormPage
                configOptions={appForm.configOptions}
                step={appForm.step}
                loanId={appForm.displayLoanId}
                activeName={appForm.activeName}
                mobile={appForm.v.phone_number}
                savedAt={savedAt}
                onBack={appForm.back}
                onGoHome={goHome}
                onGoStep={appForm.goStep}
                maxStepReached={appForm.maxStepReached}
                appStatus={appForm.appStatus}
                onCancelApplication={() => setCancelTarget({ leadId: appForm.loanId, loanNo: appForm.displayLoanId })}
                onNext={() => {
                  // Documents step: block Next until every required doc has a
                  // file, and open the category holding the first gap.
                  if (appForm.step === 3 && !appForm.isFrozen && !documents.validateRequired()) {
                    flash("Please upload all required documents");
                    return;
                  }
                  appForm.next();
                }}
                savingLead={appForm.savingLead}
                savingFinancier={appForm.savingFinancier}
                savingProperty={appForm.savingProperty}
                savingCustomer={appForm.savingCustomer}
                loadingLeadDetails={appForm.loadingLeadDetails}
                v={appForm.v}
                onFieldChange={appForm.setField}
                fieldErrors={appForm.fieldErrors}
                openAcc={appForm.openAcc}
                onToggleAcc={(key) => appForm.setOpenAcc((o) => ({ ...o, [key]: !o[key] }))}
                openCust={appForm.openCust}
                onToggleCust={(key) => appForm.setOpenCust((c) => (c === key ? null : key))}
                fins={appForm.fins}
                // Saved once → loan/employment type and the saved banks are fixed;
                // new banks can still be added (unless the lead disallows it).
                financierReadOnly={appForm.isFrozen || appForm.financierSaved}
                banksReadOnly={appForm.isFrozen || (appForm.financierSaved && !appForm.canAddFinancier)}
                lockedBanks={appForm.financierSaved ? appForm.savedFins : {}}
                isFrozen={appForm.isFrozen}
                docsLocked={appForm.docsLocked}
                allStepsOpen={appForm.allStepsOpen}
                financiers={appForm.financierList().map(appForm.toBankShape)}
                onToggleBank={(key) => {
                  const cur = !!appForm.fins[key];
                  if (cur && appForm.financierSaved && appForm.savedFins[key]) return;
                  if (!cur && Object.values(appForm.fins).filter(Boolean).length >= MAX_FINANCIERS) {
                    flash("You can't select more than " + MAX_FINANCIERS + " financiers");
                    return;
                  }
                  appForm.setFins((f) => ({ ...f, [key]: !cur }));
                  touchSaved();
                }}
                docs={documents.docs}
                docsFor={documents.docsFor}
                reqLabel={documents.reqLabel}
                categories={documents.categories}
                selectedBanks={appForm.selectedBanks}
                autoOpen={documents.autoOpen}
                onToggleAutoOpen={() => documents.setAutoOpen((o) => !o)}
                openCat={documents.openCat}
                onToggleCat={(key) => documents.setOpenCat((c) => (c === key ? null : key))}
                over={documents.over}
                drag={documents.drag}
                onDragStartDoc={(id) => documents.setDrag(id)}
                onDragEndDoc={() => {
                  documents.setDrag(null);
                  documents.setOver(null);
                }}
                onDragOverReq={(reqId) => documents.setOver((o) => (o === reqId ? o : reqId))}
                onDragLeaveReq={(reqId) => documents.setOver((o) => (o === reqId ? null : o))}
                onDropReq={(reqId, e) => {
                  if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
                    documents.receive(reqId, documents.fromFiles(e.dataTransfer.files));
                    documents.setOver(null);
                    return;
                  }
                  documents.setOver(null);
                  if (documents.drag) documents.tagTo(documents.drag, reqId);
                }}
                onTagDoc={(docId) => documents.setSheet({ kind: "req", docId })}
                onPreviewDoc={(id) => documents.setPreview(id)}
                onUploadReq={documents.pickFiles}
                onUntagDoc={(id, name) => documents.setConfirm({ kind: "untag", id, name })}
                onRemoveDoc={(id, name) => documents.setConfirm({ kind: "remove", id, name })}
                fileRef={documents.fileRef}
                onFiles={documents.onFiles}
                bankReqId={documents.bankReqId}
                govReqId={documents.govReqId}
                capFor={documents.capFor}
                categoriesLoading={documents.categoriesLoading}
                showReqErrors={documents.showReqErrors}
                uploadErrors={documents.uploadErrors}
                coborrowers={appForm.coborrowers}
                onCoborrowerFieldChange={appForm.onCoborrowerFieldChange}
                onUploadCoborrowerDoc={appForm.uploadCoborrowerDoc}
                coFieldErrors={appForm.coFieldErrors}
                openCb={appForm.openCb}
                onToggleCb={(index, key) => appForm.setOpenCb((o) => ({ ...o, [index]: o[index] === key ? null : key }))}
                onCopyBorrower={appForm.copyBorrower}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/application/created"
          element={
            <ProtectedRoute>
              <ApplicationCreatedPage
                loanId={appForm.displayLoanId}
                copied={appForm.copied}
                onCopy={appForm.copyLoanId}
                onBackToDash={toDash}
                onSendToBank={() => postApp.openPost({ loan: appForm.displayLoanId, rawId: appForm.loanId, name: appForm.v.name, stage: "send" })}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/post-application/:leadId?"
          element={
            <ProtectedRoute>
              {postApp.post ? (
                <PostApplicationPage
                  post={postApp.post}
                  onFlash={flash}
                  onSetStage={postApp.setStage}
                  onBack={postApp.leavePostApplication}
                  onGoHome={postApp.leavePostApplication}
                  savedAt={savedAt}
                  selectedBanks={appForm.selectedBanks()}
                  finTab={postApp.finTab}
                  onSetFinTab={postApp.setFinTab}
                  sent={postApp.sent}
                  sendInfo={postApp.sendInfo}
                  sendLoading={postApp.sendLoading}
                  detailsLoading={postApp.detailsLoading}
                  onPreviewSendDoc={postApp.setSendPreview}
                  sendUploadingId={postApp.sendUploadingId}
                  onUploadMissingDoc={postApp.uploadMissingSendDoc}
                  availableBanks={appForm.financierList().map(appForm.toBankShape).filter((b) => !appForm.fins[b.key])}
                  maxBanks={MAX_FINANCIERS}
                  canAddBanks={!appForm.isFrozen}
                  addingBanks={appForm.savingFinancier}
                  onAddBanks={async (keys) => {
                    const ok = await appForm.addFinanciersToLead(keys);
                    if (ok) postApp.onBanksAdded(keys);
                    return ok;
                  }}
                  docs={documents.docs}
                  docsFor={documents.docsFor}
                  reqLabel={documents.reqLabel}
                  sendExcl={postApp.sendExcl}
                  onToggleSendExcl={(id) => postApp.setSendExcl((s) => ({ ...s, [id]: !s[id] }))}
                  finExtra={postApp.finExtra}
                  onAttachExtra={postApp.attachExtra}
                  onOpenMail={postApp.openMail}
                  onPreviewDoc={(id) => documents.setPreview(id)}
                  noaStore={postApp.noaStore}
                  onSetNoaField={postApp.setNoaField}
                  noaFieldErrors={postApp.noaFieldErrors}
                  noaOpen={postApp.noaOpen}
                  onSetNoaOpen={postApp.setNoaOpen}
                  onSaveNoa={postApp.saveNoa}
                  noaUpdatable={postApp.noaUpdatable}
                  noaEditing={postApp.noaEditing}
                  onEditNoa={postApp.editNoa}
                  onCancelNoaEdit={postApp.cancelNoaEdit}
                  tabStatusLoading={postApp.tabStatusLoading}
                  contractStore={postApp.contractStore}
                  onSetContractField={postApp.setContractField}
                  contractFieldErrors={postApp.contractFieldErrors}
                  contractOpen={postApp.contractOpen}
                  onSetContractOpen={postApp.setContractOpen}
                  onSaveContract={postApp.saveContract}
                  noaMeta={postApp.noaMeta}
                  noaSaving={postApp.noaSaving}
                  noaExtracting={postApp.noaExtracting}
                  onExtractNoa={postApp.extractNoa}
                  contractMeta={postApp.contractMeta}
                  contractSaving={postApp.contractSaving}
                  onUploadDoc={postApp.uploadPostDoc}
                  onUploadNoaDoc={postApp.uploadNoaDoc}
                  bankActionSaving={postApp.bankActionSaving}
                  onDeferBank={postApp.deferBank}
                  onResumeBank={postApp.resumeBank}
                  onCancelBank={postApp.cancelBank}
                  onCancelApplication={setCancelTarget}
                />
              ) : postApp.opening ? (
                <Spinner page label="Loading application…" />
              ) : (
                <Navigate to="/dashboard" replace />
              )}
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <LeadCreatedModal
        open={appForm.leadModal}
        loanId={appForm.displayLoanId}
        copied={appForm.copied}
        onCopy={appForm.copyLoanId}
        onContinue={appForm.closeLeadModal}
        onGoDashboard={toDash}
      />

      <BottomSheet
        open={!!documents.sheet}
        title={
          documents.sheet && documents.sheet.kind === "gov"
            ? "Which government ID is this?"
            : documents.sheet && documents.sheet.kind === "bank"
            ? "Which bank is this form for?"
            : "Tag this document"
        }
        sub={
          documents.sheet && documents.sheet.kind === "gov"
            ? "Two different ID types are required — one primary, one secondary."
            : documents.sheet && documents.sheet.kind === "bank"
            ? "Each selected bank needs its own HL application form."
            : "Pick the requirement it satisfies."
        }
        items={buildSheetItems({
          sheet: documents.sheet,
          docs: documents.docs,
          docsFor: documents.docsFor,
          capFor: documents.capFor,
          reqLabel: documents.reqLabel,
          selectedBanks: appForm.selectedBanks,
          tagTo: documents.tagTo,
          tagGov: documents.tagGov,
          tagBank: documents.tagBank,
          attach: documents.attach,
          pending: documents.pending,
          govIdTypeLabels: (appForm.configOptions.govt_id_type || []).map((o) => o.label),
          setSheet: documents.setSheet,
          categories: documents.categories,
          bankReqId: documents.bankReqId,
          govReqId: documents.govReqId,
          flash,
        })}
        onClose={() => {
          documents.setSheet(null);
          documents.setPending(null);
          documents.setTarget(null);
        }}
      />

      <ConfirmDialog confirm={documents.confirm} onCancel={documents.closeConfirm} onConfirm={documents.confirmOk} />
      <ConfirmDialog
        confirm={appForm.staleFinPrompt && { title: "Financiers updated", body: STALE_FINANCIER_MESSAGE, okLabel: "Refresh" }}
        onCancel={appForm.closeStaleFinPrompt}
        onConfirm={() => window.location.reload()}
      />

      <PreviewModal
        doc={documents.previewDoc || postApp.sendPreview}
        onClose={() => {
          documents.setPreview(null);
          postApp.setSendPreview(null);
        }}
      />

      <MailModal
        open={!!postApp.finModal}
        isFollowup={postApp.finModal && postApp.finModal.kind === "followup"}
        mail={postApp.mail}
        sending={postApp.mailSending}
        loading={postApp.mailLoading}
        onChange={(k, val) => postApp.setMail((m) => ({ ...m, [k]: val }))}
        onCancel={() => postApp.setFinModal(null)}
        onSend={postApp.sendMail}
      />

      <CancelApplicationModal
        target={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={onApplicationCancelled}
        flash={flash}
      />

      <Toast message={toast} />
    </>
  );
}
